import type { Value, Tolerance, ToleranceType } from '@frontend/api';
import { TOLERANCE_TYPES } from '@frontend/api';

import { parsePrecisionValue, PrecisionValue } from './validation';

/**
 * Builds a Value object from a string input, preserving precision
 * @param stringValue - The string representation of the value
 * @returns Value object for API consumption, or undefined if invalid
 */
export const buildPrecisionValueObject = (
  stringValue: string,
): Value | undefined => {
  const parsed = parsePrecisionValue(stringValue);
  if (!parsed) {
    return undefined;
  }

  return {
    value: parsed.numericValue,
    precision: parsed.precision,
  };
};

/**
 * Configuration for building tolerance objects
 */
type ToleranceConfig = {
  toleranceType: ToleranceType;
  baseValue?: number; // Required for LIMIT type to calculate deltas
  symmetricValue?: PrecisionValue;
  upperValue?: PrecisionValue;
  lowerValue?: PrecisionValue;
};

/**
 * Builds a Tolerance object from form values
 * @param config - Configuration object with tolerance values
 * @returns Tolerance object for API consumption, or undefined if invalid
 */
export const buildToleranceObject = (
  config: ToleranceConfig,
): Tolerance | undefined => {
  const { toleranceType, baseValue, symmetricValue, upperValue, lowerValue } =
    config;

  switch (toleranceType) {
    case TOLERANCE_TYPES.SYMMETRIC:
      if (symmetricValue !== undefined) {
        return {
          tolType: TOLERANCE_TYPES.SYMMETRIC,
          upper: {
            value: symmetricValue.numericValue,
            precision: symmetricValue.precision,
          },
          lower: {
            value: symmetricValue.numericValue,
            precision: symmetricValue.precision,
          },
        };
      }
      break;

    case TOLERANCE_TYPES.ASYMMETRIC:
      if (upperValue !== undefined && lowerValue !== undefined) {
        return {
          tolType: TOLERANCE_TYPES.ASYMMETRIC,
          upper: {
            value: upperValue.numericValue,
            precision: upperValue.precision,
          },
          lower: {
            value: lowerValue.numericValue,
            precision: lowerValue.precision,
          },
        };
      }
      break;

    case TOLERANCE_TYPES.LIMIT:
      if (
        upperValue !== undefined &&
        lowerValue !== undefined &&
        baseValue !== undefined
      ) {
        return {
          tolType: TOLERANCE_TYPES.LIMIT,
          upper: {
            value: Math.abs(upperValue.numericValue - baseValue),
            precision: upperValue.precision,
          },
          lower: {
            value: Math.abs(lowerValue.numericValue - baseValue),
            precision: lowerValue.precision,
          },
        };
      }
      break;
  }

  return undefined;
};

/**
 * Builds a precision-aware measurement object with value and optional tolerance
 * @param stringValue - The string representation of the base measurement value
 * @param toleranceConfig - Optional tolerance configuration
 * @returns Object with value and optional tolerance, or undefined if invalid
 */
export const buildPrecisionMeasurementObject = (
  stringValue: string,
  toleranceConfig?: ToleranceConfig,
): { value: Value; tol?: Tolerance } | undefined => {
  const valueObject = buildPrecisionValueObject(stringValue);
  if (!valueObject) {
    return undefined;
  }

  const result: { value: Value; tol?: Tolerance } = {
    value: valueObject,
  };

  if (toleranceConfig) {
    const tolerance = buildToleranceObject(toleranceConfig);
    if (tolerance) {
      result.tol = tolerance;
    }
  }

  return result;
};

/**
 * Helper type for string-based tolerance form values
 */
export type StringToleranceFormValues = {
  hasTolerance: boolean;
  toleranceType: ToleranceType;
  toleranceSymmetric?: string;
  toleranceUpper?: string;
  toleranceLower?: string;
};

/**
 * Converts string-based tolerance form values to ToleranceConfig
 * @param formValues - String-based form values for tolerance
 * @param baseValue - Base value for LIMIT type calculations
 * @returns ToleranceConfig object or undefined if tolerance is disabled
 */
export const stringToleranceFormValuesToConfig = (
  formValues: StringToleranceFormValues,
  baseValue?: number,
): ToleranceConfig | undefined => {
  if (!formValues.hasTolerance) {
    return undefined;
  }

  // Parse string values to numbers
  const symmetricValue = formValues.toleranceSymmetric
    ? parsePrecisionValue(formValues.toleranceSymmetric)
    : undefined;
  const upperValue = formValues.toleranceUpper
    ? parsePrecisionValue(formValues.toleranceUpper)
    : undefined;
  const lowerValue = formValues.toleranceLower
    ? parsePrecisionValue(formValues.toleranceLower)
    : undefined;

  return {
    toleranceType: formValues.toleranceType,
    baseValue,
    symmetricValue: symmetricValue ?? undefined,
    upperValue: upperValue ?? undefined,
    lowerValue: lowerValue ?? undefined,
  };
};

/**
 * Helper type for multiplier form values
 */
export type MultiplierFormValues = {
  hasMult: boolean;
  mult?: number;
};

/**
 * Extracts multiplier value from form values
 * @param formValues - Form values for multiplier
 * @returns Multiplier value or undefined if disabled/invalid
 */
export const extractMultiplier = (
  formValues: MultiplierFormValues,
): number | undefined => {
  return formValues.hasMult && formValues.mult ? formValues.mult : undefined;
};
